import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/** Same-origin SignalR hub path (proxied to the API; `wss` once the page is HTTPS). */
export const EMERGENCY_HUB_PATH = '/hubs/emergency';
/** Hub client event — the active alert (or null) changed; must match `EmergencyHubGroups.AlertChangedEvent`. */
export const EMERGENCY_CHANGED_EVENT = 'emergencyChanged';

export type EmergencyType = 'Brann' | 'Vannlekkasje' | 'Evakuering' | 'Annet';
export type EmergencySeverity = 'Standard' | 'Severe';

export interface EmergencyAlertView {
  id: string;
  type: string;
  severity: string;
  title: string;
  body: string;
  status: string;
  triggeredAtUtc: string;
  resolvedAtUtc?: string | null;
  recipients: number;
  pushSent: number;
  smsSent: number;
  /** Added with the mail gateway; older alerts have none. */
  emailSent?: number;
  ackCount: number;
  iAcked: boolean;
}

export interface BeredskapItemView {
  kind: string;
  label: string;
  locationText: string;
  mapLink?: string | null;
}

export interface BeredskapView {
  items: BeredskapItemView[];
  updatedAtUtc?: string | null;
}

export interface IceContactView {
  contactName: string;
  relation: string;
  phone: string;
}

export interface TriggerAlertInput {
  type: EmergencyType;
  severity: EmergencySeverity;
  title: string;
  body: string;
}

export interface BeredskapInput {
  items: BeredskapItemView[];
}

export interface IceInput {
  contactName: string;
  relation: string;
  phone: string;
}

export interface TriggerResult {
  id: string;
  recipients: number;
}

/**
 * Phase V1.11 — nødvarsel client. The styre triggers a breakthrough alert that reaches EVERY resident
 * via push + SMS regardless of preferences (the absolute safety override); residents read the active
 * alert (live over `/hubs/emergency`, see `EmergencyStore`) and acknowledge. Beredskapsinfo is styre-
 * edited + resident-read; the optional ICE/nødkontakt is consent-gated + encrypted server-side. The
 * client never sends tenant/role/scope — every read/write is permission-scoped server-side.
 */
@Injectable({ providedIn: 'root' })
export class EmergencyService {
  private readonly http = inject(HttpClient);

  /** The current active alert, or null (204) when there is none. */
  active(): Observable<EmergencyAlertView | null> {
    return this.http.get<EmergencyAlertView | null>('/api/emergency/active');
  }

  ack(alertId: string, channel = 'app'): Observable<void> {
    return this.http.post<void>(`/api/emergency/alerts/${encodeURIComponent(alertId)}/ack`, { channel });
  }

  beredskap(): Observable<BeredskapView> {
    return this.http.get<BeredskapView>('/api/emergency/beredskap');
  }

  registerIce(input: IceInput): Observable<void> {
    return this.http.post<void>('/api/emergency/ice', input);
  }

  // ── styre-only ──

  trigger(input: TriggerAlertInput): Observable<TriggerResult> {
    return this.http.post<TriggerResult>('/api/emergency/alerts', input);
  }

  resolve(alertId: string): Observable<void> {
    return this.http.post<void>(`/api/emergency/alerts/${encodeURIComponent(alertId)}/resolve`, {});
  }

  history(): Observable<EmergencyAlertView[]> {
    return this.http.get<EmergencyAlertView[]>('/api/emergency/alerts');
  }

  setBeredskap(input: BeredskapInput): Observable<void> {
    return this.http.put<void>('/api/emergency/beredskap', input);
  }

}
